import { useTheme } from "@emotion/react";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { MdKeyboardArrowDown } from "react-icons/md";

import { SupportSelectListbox } from "./SupportSelectListbox";
import type { SupportSelectOption, SupportSelectVariant } from "./supportSelectTypes";
import { useSupportSelectKeyboard } from "./useSupportSelectKeyboard";

export type { SupportSelectOption, SupportSelectVariant } from "./supportSelectTypes";
export { SupportSelectDot } from "./SupportSelectListbox";

type Props<T extends string> = {
  value: T | null | undefined;
  options: SupportSelectOption<T>[];
  onChange: (value: T | null) => void;
  isDisabled?: boolean;
  label?: string;
  placeholder?: string;
  variant?: SupportSelectVariant;
};

export function SupportSelect<T extends string>({
  value,
  options,
  onChange,
  isDisabled,
  label,
  placeholder,
  variant = "bold",
}: Props<T>) {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const listboxRef = useRef<HTMLUListElement | null>(null);
  const listboxId = useId();
  const optionIdPrefix = useId();

  const optionKeys = useMemo(
    () => options.map((option, index) => option.value ?? `__none__-${index}`),
    [options],
  );

  const currentOption = options.find((option) => option.value === (value ?? null)) ?? null;
  const selectedIndex = currentOption ? options.indexOf(currentOption) : -1;

  useEffect(() => {
    if (!isOpen) return;
    const handleOutsideClick = (event: MouseEvent) => {
      const path = event.composedPath();
      const root = dropdownRef.current;
      if (root && !path.includes(root)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [isOpen, selectedIndex]);

  useEffect(() => {
    if (!isOpen || activeIndex < 0) return;
    const listbox = listboxRef.current;
    if (!listbox) return;
    const activeItem = listbox.children[activeIndex] as HTMLElement | undefined;
    activeItem?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, isOpen]);

  const openMenu = useCallback(() => {
    if (isDisabled) return;
    setIsOpen(true);
  }, [isDisabled]);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    buttonRef.current?.focus();
  }, []);

  const selectAt = useCallback(
    (index: number) => {
      const option = options[index];
      if (!option) return;
      onChange(option.value);
      setIsOpen(false);
      buttonRef.current?.focus();
    },
    [onChange, options],
  );

  const handleKeyDown = useSupportSelectKeyboard({
    isDisabled,
    isOpen,
    openMenu,
    closeMenu,
    selectAt,
    setActiveIndex,
    setIsOpen,
    activeIndex,
    optionsLength: options.length,
  });

  const triggerBackground = variant === "form" ? theme.colors.primaryLight : "white";
  const triggerStyles =
    variant === "form"
      ? {
          borderRadius: theme.smallBorderRadius,
          padding: "0.4375rem 0.5rem",
          border: "none",
          background: triggerBackground,
          fontSize: "1rem",
          fontWeight: 400,
        }
      : {
          borderRadius: theme.smallBorderRadius,
          padding: "10px 12px",
          border: `1px solid ${theme.colors.border}`,
          background: triggerBackground,
          fontSize: "14px",
          fontWeight: 700,
        };

  const triggerLabel = currentOption?.label ?? placeholder ?? "";
  const triggerColor = currentOption?.color ?? theme.colors.secondaryText;
  const activeOptionId =
    isOpen && activeIndex >= 0 ? `${optionIdPrefix}-${optionKeys[activeIndex]}` : undefined;

  const trigger = (
    <button
      ref={buttonRef}
      type="button"
      onClick={() => (isOpen ? setIsOpen(false) : openMenu())}
      onKeyDown={handleKeyDown}
      disabled={isDisabled}
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      aria-controls={isOpen ? listboxId : undefined}
      aria-activedescendant={activeOptionId}
      aria-disabled={isDisabled || undefined}
      css={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        cursor: isDisabled ? "not-allowed" : "pointer",
        width: "100%",
        gap: "8px",
        opacity: isDisabled ? 0.7 : 1,
        ...triggerStyles,
        "&:focus-visible": {
          outline: `2px solid ${theme.colors.primary}`,
          outlineOffset: variant === "form" ? "0" : "-1px",
        },
      }}
    >
      <span
        css={{
          color: triggerColor,
          display: "flex",
          gap: "8px",
          alignItems: "center",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {currentOption?.icon}
        {triggerLabel}
      </span>
      <MdKeyboardArrowDown size={18} color={theme.colors.secondaryText} />
    </button>
  );

  const dropdown = isOpen && !isDisabled && (
    <SupportSelectListbox
      listboxRef={listboxRef}
      listboxId={listboxId}
      optionIdPrefix={optionIdPrefix}
      optionKeys={optionKeys}
      options={options}
      selectedIndex={selectedIndex}
      activeIndex={activeIndex}
      variant={variant}
      triggerBackground={triggerBackground}
      onActiveIndexChange={setActiveIndex}
      onSelect={selectAt}
    />
  );

  if (label) {
    return (
      <div ref={dropdownRef} css={{ display: "flex", flexDirection: "column", gap: "4px", position: "relative" }}>
        <span
          id={`${listboxId}-label`}
          css={{
            fontFamily: theme.displayFontFamily,
            fontSize: "14px",
            color: theme.colors.primary,
          }}
          onClick={() => buttonRef.current?.focus()}
        >
          {label}
        </span>
        {trigger}
        {dropdown}
      </div>
    );
  }

  return (
    <div ref={dropdownRef} css={{ position: "relative" }}>
      {trigger}
      {dropdown}
    </div>
  );
}
